import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./components/App";
import { Theme } from "@radix-ui/themes";
import "@radix-ui/themes/styles.css";

/* global document, Office, module, require, window */

const title = "Refly Outlook Add-in";

const rootElement = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

const isColorDark = (hex: string) => {
  if (!hex || hex.length < 7) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
};

const ThemeWrapper = ({ children }: { children: React.ReactNode }) => {
  const [isDark, setIsDark] = React.useState(false);

  const updateTheme = React.useCallback(() => {
    // 1. Try Office Theme (Outlook Desktop/Web)
    if (Office.context && Office.context.officeTheme) {
      const theme = Office.context.officeTheme;
      if (theme.bodyBackgroundColor) {
        setIsDark(isColorDark(theme.bodyBackgroundColor));
        return;
      }
    }

    // 2. Fallback to System Preference
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setIsDark(mediaQuery.matches);
  }, []);

  React.useEffect(() => {
    updateTheme();

    // Listen for Office Theme changes
    if (Office.context && Office.context.mailbox && Office.context.mailbox.addHandlerAsync) {
      Office.context.mailbox.addHandlerAsync(Office.EventType.OfficeThemeChanged, updateTheme);
    }

    // Listen for System Theme changes (fallback)
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      // Only apply system change if Office theme isn't providing a specific background color
      if (!Office.context?.officeTheme?.bodyBackgroundColor) {
        setIsDark(e.matches);
      }
    };
    mediaQuery.addEventListener("change", handler);

    return () => {
      if (Office.context && Office.context.mailbox && Office.context.mailbox.removeHandlerAsync) {
        Office.context.mailbox.removeHandlerAsync(Office.EventType.OfficeThemeChanged, updateTheme);
      }
      mediaQuery.removeEventListener("change", handler);
    };
  }, [updateTheme]);

  return <Theme appearance={isDark ? "dark" : "light"}>{children}</Theme>;
};

/* Render application after Office initializes */
Office.onReady(() => {
  const renderApp = () => {
    // Generate a unique key based on the current item to force App remount on item change
    const itemKey = Office.context?.mailbox?.item?.itemId || Date.now().toString();
    
    root?.render(
      <ThemeWrapper>
        <App key={itemKey} title={title} />
      </ThemeWrapper>
    );
  };

  if (Office.context && Office.context.mailbox && Office.context.mailbox.addHandlerAsync) {
    // Required to enable task pane pinning in Outlook
    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => {
      console.log("Outlook item changed");
      renderApp();
    }, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        console.error(`Failed to register handler: ${asyncResult.error.message}`);
      }
      renderApp();
    });
  } else {
    renderApp();
  }
});

if (module.hot) {
  module.hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(
      <ThemeWrapper>
        <NextApp title={title} />
      </ThemeWrapper>
    );
  });
}
